import { useState } from 'react';
import { Navigate, useLocation, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/components/auth/AuthProvider';
import { Loader2, Shield, Eye, EyeOff } from 'lucide-react';
import {getImageUrl} from '@/lib/utils';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { user, signIn } = useAuth();
  const location = useLocation();
  const isAuditsLogin = location.pathname.includes('/audits/admin');
  const isIncorpifyLogin = location.pathname.includes('/incorpify/admin');
  const isTenantLogin = isAuditsLogin || isIncorpifyLogin;

  // Redirect if already logged in
  if (user) {
    // If the login page is a tenant-specific admin login, default to its dashboard
    const fallback = isAuditsLogin
      ? '/audits/admin'
      : isIncorpifyLogin
        ? '/incorpify/admin'
        : '/system/dashboard';
    const from = location.state?.from?.pathname || fallback;
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const { error } = await signIn(email, password);

    if (error) {
      setError(error.message);
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            <Link to="/">
              {/* Desktop logo */}
              <img
                src={getImageUrl("yallacompare-for-business-logo.png")}
                alt="Yallacompare for Business"
                className="h-36 w-auto object-contain mx-auto hidden sm:block"
              />
              {/* Mobile logo */}
              <img
                src={getImageUrl("yallacompare-for-business-logo.png")}
                alt="Yallacompare for Business"
                className="h-36 w-auto object-contain mx-auto sm:hidden"
              />
            </Link>
          </div>
          <CardTitle className="text-2xl font-bold">Admin Login</CardTitle>
          <CardDescription>
            Access the business insurance admin dashboard
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">{isTenantLogin ? 'Username or Email' : 'Email'}</Label>
              <Input
                id="email"
                type={isTenantLogin ? 'text' : 'email'}
                placeholder={isAuditsLogin ? 'audits or audits@yallacompare.com' : isIncorpifyLogin ? 'incorpify or incorpify@yallacompare.com' : 'admin@example.com'}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  className="pr-10"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

        </CardContent>
      </Card>
    </div>
  );
}
